import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import {
  GraduationCap, Plus, Users, Calendar, Phone, ArrowLeft,
  ClipboardCheck, Loader2, CheckCircle, Star, AlertTriangle, Pencil, MessageSquare, Eye,
} from 'lucide-react';
import { toast } from 'sonner';
import { API_URL } from '@/config/api';
import { useCelebration } from '@/hooks/useCelebration';
import CourseRegister from '@/components/training/CourseRegister';
import MessageSettingsDialog from '@/components/training/MessageSettingsDialog';
import LeadDetailDrawer from '@/components/training/LeadDetailDrawer';

const DAYS_OF_WEEK = [
  { value: 'monday', label: 'Mon' },
  { value: 'tuesday', label: 'Tue' },
  { value: 'wednesday', label: 'Wed' },
  { value: 'thursday', label: 'Thu' },
  { value: 'friday', label: 'Fri' },
  { value: 'saturday', label: 'Sat' },
  { value: 'sunday', label: 'Sun' },
];

const DAY_ORDER = DAYS_OF_WEEK.map(d => d.value);
const shortDay = (d) => d.charAt(0).toUpperCase() + d.slice(1, 3);

const getDayTimes = (course, day) => {
  const t = course?.day_schedule?.[day];
  return {
    start: t?.start || course?.class_time_start || '09:00',
    end: t?.end || course?.class_time_end || '16:00',
  };
};

const getScheduleLines = (course) => {
  const days = [...(course?.class_days || [])].sort((a, b) => DAY_ORDER.indexOf(a) - DAY_ORDER.indexOf(b));
  if (days.length === 0) return [];
  const times = days.map(d => getDayTimes(course, d));
  const allSame = times.every(t => t.start === times[0].start && t.end === times[0].end);
  if (allSame) return [`${days.map(shortDay).join(', ')} • ${times[0].start} - ${times[0].end}`];
  return days.map((d, i) => `${shortDay(d)} ${times[i].start} - ${times[i].end}`);
};

const EMPTY_COURSE_FORM = {
  name: '', accreditation: '', start_date: '', end_date: '',
  class_days: [], class_time_start: '09:00', class_time_end: '16:00',
  capacity: 20, location: '', per_day_times: false, day_schedule: {},
  price: '', cost_per_student: '', offer_group: '',
};

const toMoney = (v) => (v === '' || v === null || v === undefined || Number.isNaN(parseFloat(v)) ? null : parseFloat(v));

const STATUS_COLORS = {
  upcoming: 'bg-blue-100 text-blue-700',
  in_progress: 'bg-green-100 text-green-700',
  completed: 'bg-slate-100 text-slate-600',
};

const CoursesPage = () => {
  const { user, selectedBusiness } = useAuth();
  const canEdit = ['admin', 'super_admin'].includes(user?.role);
  const { celebrate } = useCelebration();
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);

  // Views: 'list' | 'detail' | 'attendance'
  const [view, setView] = useState('list');
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [courseDetail, setCourseDetail] = useState(null);

  // Create course dialog
  const [createDialog, setCreateDialog] = useState(false);
  const [creating, setCreating] = useState(false);
  const [courseForm, setCourseForm] = useState(EMPTY_COURSE_FORM);
  const [editingCourse, setEditingCourse] = useState(null);

  const [messagesOpen, setMessagesOpen] = useState(false);
  const [insights, setInsights] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [registerDate, setRegisterDate] = useState(null);
  const [drawerLeadId, setDrawerLeadId] = useState(null);

  const studentDrawer = (
    <LeadDetailDrawer
      open={!!drawerLeadId}
      onOpenChange={(o) => { if (!o) setDrawerLeadId(null); }}
      leadId={drawerLeadId}
      onLeadUpdated={() => { if (selectedCourse) fetchCourseDetail(selectedCourse.course_id); }}
    />
  );

  const token = localStorage.getItem('token');
  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  const fetchCourses = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/training/courses`, {
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include',
      });
      if (res.ok) setCourses(await res.json());
    } catch (err) {
      console.error('Courses fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  const fetchInsights = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/training/attendance-insights`, {
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include',
      });
      if (res.ok) setInsights(await res.json());
    } catch { /* optional panel */ }
  }, [token]);

  useEffect(() => {
    fetchCourses();
    fetchInsights();
  }, [fetchCourses, fetchInsights, selectedBusiness?.business_id]);

  const fetchCourseDetail = async (courseId) => {
    try {
      const res = await fetch(`${API_URL}/api/training/courses/${courseId}`, {
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include',
      });
      if (res.ok) setCourseDetail(await res.json());
    } catch (err) {
      console.error('Course detail error:', err);
    }
  };

  const openCourseDetail = (course) => {
    setSelectedCourse(course);
    fetchCourseDetail(course.course_id);
    setView('detail');
  };

  const completeAll = async (f) => {
    if (!window.confirm(`Mark all ${f.students} students on "${f.course}" as completed? Each one gets their Google review request.`)) return;
    const res = await fetch(`${API_URL}/api/training/courses/${f.course_id}/complete-all`, { method: 'POST', headers, credentials: 'include' });
    const body = await res.json().catch(() => ({}));
    if (res.ok) {
      celebrate();
      toast.success(`${body.completed} students marked complete`);
      fetchCourses();
      fetchInsights();
    } else {
      toast.error(body.detail || 'Could not complete the course');
    }
  };

  const openAttendance = (course, date = null) => {
    setSelectedCourse(course);
    setRegisterDate(date);
    setView('attendance');
  };

  // Deep link from the calendar: /courses?register=<course_id>&date=YYYY-MM-DD
  useEffect(() => {
    const courseId = searchParams.get('register');
    if (!courseId || courses.length === 0) return;
    const course = courses.find(c => c.course_id === courseId);
    if (course) openAttendance(course, searchParams.get('date'));
    setSearchParams({}, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courses, searchParams]);

  const openCreateDialog = () => {
    setEditingCourse(null);
    setCourseForm(EMPTY_COURSE_FORM);
    setCreateDialog(true);
  };

  const openEditDialog = (course) => {
    const days = course.class_days || [];
    const daySchedule = {};
    days.forEach(d => { daySchedule[d] = getDayTimes(course, d); });
    const times = days.map(d => daySchedule[d]);
    const perDay = times.length > 1 && !times.every(t => t.start === times[0].start && t.end === times[0].end);
    setEditingCourse(course);
    setCourseForm({
      name: course.name || '',
      accreditation: course.accreditation || '',
      start_date: course.start_date || '',
      end_date: course.end_date || '',
      class_days: days,
      class_time_start: course.class_time_start || '09:00',
      class_time_end: course.class_time_end || '16:00',
      capacity: course.capacity || 20,
      location: course.location || '',
      per_day_times: perDay,
      day_schedule: daySchedule,
      price: course.price ?? '',
      cost_per_student: course.cost_per_student ?? '',
      offer_group: course.offer_group || '',
    });
    setCreateDialog(true);
  };

  const buildCoursePayload = (form) => {
    const days = [...form.class_days].sort((a, b) => DAY_ORDER.indexOf(a) - DAY_ORDER.indexOf(b));
    let daySchedule = null;
    let start = form.class_time_start;
    let end = form.class_time_end;
    if (form.per_day_times) {
      daySchedule = {};
      days.forEach(d => {
        const t = form.day_schedule[d] || { start: form.class_time_start, end: form.class_time_end };
        daySchedule[d] = { start: t.start, end: t.end };
      });
      if (days.length > 0) {
        start = daySchedule[days[0]].start;
        end = daySchedule[days[0]].end;
      }
    }
    return {
      name: form.name,
      accreditation: form.accreditation,
      start_date: form.start_date,
      end_date: form.end_date,
      class_days: days,
      class_time_start: start,
      class_time_end: end,
      day_schedule: daySchedule,
      capacity: form.capacity,
      location: form.location,
      price: toMoney(form.price),
      cost_per_student: toMoney(form.cost_per_student),
      offer_group: form.offer_group || null,
      description: editingCourse?.description ?? null,
      ghl_calendar_id: editingCourse?.ghl_calendar_id ?? null,
    };
  };

  const handleSaveCourse = async () => {
    if (!courseForm.name || !courseForm.start_date || !courseForm.end_date || courseForm.class_days.length === 0) {
      toast.error('Please fill in course name, dates, and class days');
      return;
    }
    const payload = buildCoursePayload(courseForm);
    if (payload.end_date < payload.start_date) {
      toast.error('End date must be after the start date');
      return;
    }
    const badDay = payload.class_days.find(d => {
      const t = payload.day_schedule ? payload.day_schedule[d] : { start: payload.class_time_start, end: payload.class_time_end };
      return !t.start || !t.end || t.end <= t.start;
    });
    if (badDay) {
      toast.error(`Check the times for ${shortDay(badDay)} — end time must be after start time`);
      return;
    }
    const isEdit = !!editingCourse;
    setCreating(true);
    try {
      const res = await fetch(
        isEdit ? `${API_URL}/api/training/courses/${editingCourse.course_id}` : `${API_URL}/api/training/courses`,
        { method: isEdit ? 'PUT' : 'POST', headers, credentials: 'include', body: JSON.stringify(payload) },
      );
      if (res.ok) {
        const saved = await res.json();
        toast.success(isEdit ? 'Course updated!' : 'Course created!');
        setCreateDialog(false);
        if (!isEdit) pushCourseToGhl(payload);
        if (isEdit && selectedCourse?.course_id === saved.course_id) {
          setSelectedCourse(saved);
          fetchCourseDetail(saved.course_id);
        }
        setEditingCourse(null);
        setCourseForm(EMPTY_COURSE_FORM);
        fetchCourses();
      } else {
        const data = await res.json().catch(() => ({}));
        toast.error(typeof data.detail === 'string' ? data.detail : `Failed to ${isEdit ? 'update' : 'create'} course`);
      }
    } catch (err) {
      toast.error(`Failed to ${isEdit ? 'update' : 'create'} course`);
    } finally {
      setCreating(false);
    }
  };

  const pushCourseToGhl = async (form) => {
    try {
      // Fetch GHL calendars to get the default calendar_id
      const calRes = await fetch(`${API_URL}/api/training/ghl/calendars`, {
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include',
      });
      if (!calRes.ok) return; // GHL not connected, skip silently
      const calData = await calRes.json();
      const calendars = calData.calendars || [];
      if (calendars.length === 0) return;

      const calendarId = calendars[0].id;
      const dayMap = { sunday: 0, monday: 1, tuesday: 2, wednesday: 3, thursday: 4, friday: 5, saturday: 6 };

      // Generate all class dates between start and end
      const start = new Date(form.start_date + 'T00:00:00');
      const end = new Date(form.end_date + 'T23:59:59');
      const classDates = [];

      for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
        const dayOfWeek = d.getDay();
        if (form.class_days.some(cd => dayMap[cd] === dayOfWeek)) {
          classDates.push(new Date(d));
        }
      }

      // Push each class day (fire-and-forget, batch of 5 at a time to avoid overload)
      let pushed = 0;
      for (let i = 0; i < classDates.length; i += 5) {
        const batch = classDates.slice(i, i + 5);
        await Promise.all(batch.map(date => {
          const dateStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
          const dayName = DAY_ORDER[(date.getDay() + 6) % 7];
          const times = getDayTimes(form, dayName);
          const startTime = new Date(`${dateStr}T${times.start}:00`).toISOString();
          const endTime = new Date(`${dateStr}T${times.end}:00`).toISOString();
          return fetch(`${API_URL}/api/training/ghl/calendar-events`, {
            method: 'POST',
            headers,
            credentials: 'include',
            body: JSON.stringify({
              calendar_id: calendarId,
              title: `${form.name} - Class`,
              start_time: startTime,
              end_time: endTime,
              notes: form.location ? `Location: ${form.location}` : undefined,
            }),
          });
        }));
        pushed += batch.length;
      }
      if (pushed > 0) {
        toast.success(`${pushed} class days added to the calendar`);
      }
    } catch (err) {
      console.error('GHL course push error:', err);
      // Don't show error toast — course was already created successfully
    }
  };

  const toggleDay = (day) => {
    setCourseForm(prev => {
      const removing = prev.class_days.includes(day);
      const daySchedule = { ...prev.day_schedule };
      if (removing) delete daySchedule[day];
      else if (!daySchedule[day]) daySchedule[day] = { start: prev.class_time_start, end: prev.class_time_end };
      return {
        ...prev,
        class_days: removing ? prev.class_days.filter(d => d !== day) : [...prev.class_days, day],
        day_schedule: daySchedule,
      };
    });
  };

  const setDayTime = (day, field, value) => {
    setCourseForm(prev => ({
      ...prev,
      day_schedule: {
        ...prev.day_schedule,
        [day]: { ...(prev.day_schedule[day] || { start: prev.class_time_start, end: prev.class_time_end }), [field]: value },
      },
    }));
  };

  const togglePerDayTimes = () => {
    setCourseForm(prev => {
      const daySchedule = { ...prev.day_schedule };
      prev.class_days.forEach(d => {
        if (!daySchedule[d]) daySchedule[d] = { start: prev.class_time_start, end: prev.class_time_end };
      });
      return { ...prev, per_day_times: !prev.per_day_times, day_schedule: daySchedule };
    });
  };

  const handleMarkCompleted = async (leadId, courseId) => {
    try {
      const res = await fetch(`${API_URL}/api/training/reviews/request`, {
        method: 'POST',
        headers,
        credentials: 'include',
        body: JSON.stringify({ lead_id: leadId, course_id: courseId }),
      });
      if (res.ok) {
        celebrate();
        toast.success('Course marked as completed! Review request sent.');
        fetchCourseDetail(courseId);
        fetchCourses();
      } else {
        const data = await res.json();
        toast.error(data.detail || 'Failed to mark completed');
      }
    } catch (err) {
      toast.error('Failed to mark completed');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="w-8 h-8 border-4 border-pink-400 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // ============ ATTENDANCE VIEW ============
  if (view === 'attendance' && selectedCourse) {
    return (
      <>
        <CourseRegister course={selectedCourse} initialDate={registerDate} onOpenStudent={setDrawerLeadId} onBack={() => { setView('list'); fetchCourses(); fetchInsights(); }} />
        {studentDrawer}
      </>
    );
  }

  // ============ COURSE DETAIL VIEW ============
  if (view === 'detail' && selectedCourse) {
    const enrollments = courseDetail?.enrollments || [];
    return (
      <div className="space-y-6 animate-fade-in">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => setView('list')} className="rounded-xl">
            <ArrowLeft className="w-4 h-4 mr-1" /> Back
          </Button>
          <div className="flex-1">
            <h1 className="text-xl sm:text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {selectedCourse.name}
            </h1>
            {selectedCourse.accreditation && (
              <p className="text-sm text-slate-500">{selectedCourse.accreditation}</p>
            )}
          </div>
          <Button
            variant="outline"
            className="rounded-xl"
            onClick={() => openEditDialog(courseDetail || selectedCourse)}
          >
            <Pencil className="w-4 h-4 mr-2" />
            Edit
          </Button>
          <Button
            className="btn-primary rounded-xl"
            onClick={() => openAttendance(selectedCourse)}
          >
            <ClipboardCheck className="w-4 h-4 mr-2" />
            Mark Register
          </Button>
        </div>

        {/* Course Info */}
        <Card className="border-0 shadow-sm">
          <CardContent className="p-5">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
              <div>
                <p className="text-xs text-slate-400">Dates</p>
                <p className="font-medium">
                  {new Date(selectedCourse.start_date + 'T00:00:00').toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                  {' — '}
                  {new Date(selectedCourse.end_date + 'T00:00:00').toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                </p>
              </div>
              <div className="col-span-2">
                <p className="text-xs text-slate-400">Schedule</p>
                {getScheduleLines(selectedCourse).map(line => (
                  <p key={line} className="font-medium">{line}</p>
                ))}
              </div>
              <div>
                <p className="text-xs text-slate-400">Capacity</p>
                <p className="font-medium">{enrollments.length}/{selectedCourse.capacity}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Attendance Alert */}
        {(() => {
          const atRisk = enrollments.filter(e => (e.attendance_percentage || 0) < 80 && (e.total_classes || 0) >= 3);
          if (atRisk.length === 0) return null;
          return (
            <Card className="border-0 shadow-sm border-l-4 border-l-red-400">
              <CardContent className="p-4 flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-red-50 flex items-center justify-center flex-shrink-0">
                  <AlertTriangle className="w-5 h-5 text-red-500" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-red-700">
                    {atRisk.length} student{atRisk.length > 1 ? 's' : ''} below 80% attendance
                  </p>
                  <p className="text-xs text-red-500">
                    {atRisk.map(e => e.lead_name).join(', ')}
                  </p>
                </div>
              </CardContent>
            </Card>
          );
        })()}

        {/* Enrolled Students */}
        <div>
          <h2 className="text-base font-semibold text-slate-800 mb-3">
            Enrolled Students ({enrollments.length})
          </h2>
          {enrollments.length === 0 ? (
            <Card className="border-0 shadow-sm">
              <CardContent className="py-8 text-center">
                <Users className="w-10 h-10 text-slate-300 mx-auto mb-2" />
                <p className="text-sm text-slate-400">No students enrolled yet</p>
                <p className="text-xs text-slate-400 mt-1">Students are enrolled when they complete registration</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-2">
              {enrollments.map((enr) => (
                <Card key={enr.enrollment_id} className="border-0 shadow-sm">
                  <CardContent className="p-4 flex items-center justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <button type="button" onClick={() => setDrawerLeadId(enr.lead_id)} className="text-sm font-medium text-slate-900 hover:text-pink-600 text-left">
                          {enr.lead_name}
                        </button>
                        {(enr.attendance_percentage || 0) < 80 && (enr.total_classes || 0) >= 3 && (
                          <Badge className="text-xs bg-red-100 text-red-600">Below 80%</Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-3 mt-1">
                        <div className="flex-1 max-w-[120px]">
                          <Progress value={enr.attendance_percentage || 0} className={`h-1.5 ${(enr.attendance_percentage || 0) < 80 && (enr.total_classes || 0) >= 3 ? '[&>div]:bg-red-500' : ''}`} />
                        </div>
                        <span className={`text-xs ${(enr.attendance_percentage || 0) < 80 && (enr.total_classes || 0) >= 3 ? 'text-red-500 font-semibold' : 'text-slate-400'}`}>
                          {enr.attendance_percentage || 0}% ({enr.classes_attended || 0}/{enr.total_classes || 0})
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge className={`text-xs ${enr.status === 'enrolled' ? 'bg-green-100 text-green-700' : enr.status === 'completed' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'}`}>
                        {enr.status}
                      </Badge>
                      {enr.status === 'enrolled' && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7 text-xs rounded-lg border-emerald-200 text-emerald-700 hover:bg-emerald-50"
                          onClick={() => handleMarkCompleted(enr.lead_id, selectedCourse.course_id)}
                        >
                          <Star className="w-3 h-3 mr-1" />
                          Complete
                        </Button>
                      )}
                      {enr.lead_phone && (
                        <a href={`tel:${enr.lead_phone}`} className="w-8 h-8 rounded-full bg-pink-100 text-pink-600 flex items-center justify-center hover:bg-pink-200">
                          <Phone className="w-3.5 h-3.5" />
                        </a>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
        {studentDrawer}
      </div>
    );
  }

  // ============ COURSES LIST VIEW ============
  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Courses
          </h1>
          <p className="text-slate-500 mt-1">Manage courses and track attendance</p>
        </div>
        <div className="flex items-center gap-2">
          {canEdit && (
            <Button variant="outline" className="rounded-xl h-11" onClick={() => setMessagesOpen(true)}>
              <MessageSquare className="w-4 h-4 mr-2" />
              Messages
            </Button>
          )}
          <Button className="btn-primary rounded-xl px-5 h-11" onClick={openCreateDialog}>
            <Plus className="w-4 h-4 mr-2" />
            Add Course
          </Button>
        </div>
      </div>

      {insights && (insights.missing_registers.length > 0 || insights.at_risk_students.length > 0 || insights.low_attendance_courses.length > 0 || (insights.finished_courses || []).length > 0) && (
        <Card className="border-0 shadow-sm border-l-4 border-l-amber-400">
          <CardContent className="p-4 space-y-3">
            <p className="flex items-center gap-2 text-sm font-semibold text-slate-900">
              <Eye className="w-4 h-4 text-amber-500" /> Things to look at
            </p>
            {insights.missing_registers.length > 0 && (
              <div>
                <p className="text-xs font-medium text-amber-700 mb-1">Registers not taken ({insights.missing_registers.length})</p>
                <div className="flex flex-wrap gap-1.5">
                  {insights.missing_registers.slice(0, 8).map(m => (
                    <button key={`${m.course_id}-${m.date}`} type="button"
                      onClick={() => { const c = courses.find(x => x.course_id === m.course_id); if (c) openAttendance(c); }}
                      className="text-xs rounded-lg bg-amber-50 text-amber-800 px-2 py-1 hover:bg-amber-100">
                      {m.course} · {new Date(`${m.date}T00:00:00`).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {insights.at_risk_students.length > 0 && (
              <div>
                <p className="text-xs font-medium text-red-700 mb-1">Students at risk ({insights.at_risk_students.length})</p>
                <div className="flex flex-wrap gap-1.5">
                  {insights.at_risk_students.slice(0, 8).map(s => (
                    <button key={s.enrollment_id} type="button"
                      onClick={() => { const c = courses.find(x => x.course_id === s.course_id); if (c) openAttendance(c); }}
                      className="text-xs rounded-lg bg-red-50 text-red-700 px-2 py-1 hover:bg-red-100">
                      {s.name} · {s.percentage != null ? `${s.percentage}%` : ''}{s.absent_streak >= 2 ? ` · ${s.absent_streak} absences in a row` : ''}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {(insights.finished_courses || []).length > 0 && (
              <div>
                <p className="text-xs font-medium text-blue-700 mb-1">Finished courses with students still enrolled</p>
                <div className="space-y-1">
                  {insights.finished_courses.map(f => (
                    <div key={f.course_id} className="flex flex-wrap items-center justify-between gap-2 rounded-lg bg-blue-50 px-2 py-1.5">
                      <span className="text-xs text-blue-800">
                        {f.course} · ended {new Date(`${f.end_date}T00:00:00`).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })} · {f.students} still enrolled
                      </span>
                      {canEdit && (
                        <Button size="sm" className="h-7 text-xs rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => completeAll(f)}>
                          Mark all complete
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
            {insights.low_attendance_courses.length > 0 && (
              <p className="text-xs text-slate-600">
                Low attendance: {insights.low_attendance_courses.map(c => `${c.course} (${c.average_attendance}%)`).join(', ')}
              </p>
            )}
          </CardContent>
        </Card>
      )}

      <MessageSettingsDialog open={messagesOpen} onOpenChange={setMessagesOpen} />
      {studentDrawer}

      {courses.length === 0 ? (
        <Card className="border-0 shadow-sm">
          <CardContent className="py-12 text-center">
            <GraduationCap className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500 mb-4">No courses yet. Create your first course!</p>
            <Button className="btn-primary rounded-xl" onClick={openCreateDialog}>
              <Plus className="w-4 h-4 mr-2" />
              Add Course
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {courses.map((course) => (
            <Card
              key={course.course_id}
              className="border-0 shadow-sm hover:shadow-md transition-shadow cursor-pointer"
              onClick={() => openCourseDetail(course)}
            >
              <CardContent className="p-5">
                <div className="flex items-start justify-between mb-3">
                  <div className="w-10 h-10 rounded-xl bg-pink-50 flex items-center justify-center">
                    <GraduationCap className="w-5 h-5 text-pink-500" />
                  </div>
                  <Badge className={`text-xs ${STATUS_COLORS[course.status] || 'bg-slate-100 text-slate-600'}`}>
                    {course.status?.replace('_', ' ')}
                  </Badge>
                </div>
                <div className="flex items-start justify-between gap-2 mb-1">
                  <h3 className="font-semibold text-slate-900">{course.name}</h3>
                  {course.price != null && (
                    <span className="text-sm font-semibold text-pink-600 whitespace-nowrap">£{Number(course.price).toLocaleString('en-GB')}</span>
                  )}
                </div>
                {course.accreditation && (
                  <p className="text-xs text-slate-500 mb-3">{course.accreditation}</p>
                )}
                <div className="flex items-center gap-4 text-xs text-slate-500 mb-3">
                  <span className="flex items-center gap-1">
                    <Calendar className="w-3 h-3" />
                    {new Date(course.start_date + 'T00:00:00').toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                    {' - '}
                    {new Date(course.end_date + 'T00:00:00').toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                  </span>
                  <span className="flex items-center gap-1">
                    <Users className="w-3 h-3" />
                    {course.enrolled_count || 0}/{course.capacity}
                  </span>
                </div>
                <Progress value={((course.enrolled_count || 0) / course.capacity) * 100} className="h-2" />
                <div className="flex items-center justify-between gap-2 mt-3">
                  <div className="text-xs text-slate-400">
                    {getScheduleLines(course).map(line => <p key={line}>{line}</p>)}
                  </div>
                  {course.status !== 'completed' && (
                    <Button
                      size="sm"
                      className="h-7 text-xs rounded-lg bg-green-600 hover:bg-green-700 text-white"
                      onClick={(e) => { e.stopPropagation(); openAttendance(course); }}
                    >
                      <ClipboardCheck className="w-3 h-3 mr-1" />
                      Register
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create Course Dialog */}
      <Dialog open={createDialog} onOpenChange={setCreateDialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>{editingCourse ? 'Edit Course' : 'Add New Course'}</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-700">Course Name</label>
              <Input
                value={courseForm.name}
                onChange={(e) => setCourseForm(p => ({ ...p, name: e.target.value }))}
                placeholder="e.g. Level 3 Nail Technology"
                className="rounded-xl"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-700">Offer type (optional)</label>
              <Input
                list="offer-types"
                value={courseForm.offer_group}
                onChange={(e) => setCourseForm(p => ({ ...p, offer_group: e.target.value }))}
                placeholder="e.g. Fast Track, Regulated pathway"
                maxLength={60}
                className="rounded-xl"
              />
              <datalist id="offer-types">
                {[...new Set(['Fast Track', 'Regulated pathway', 'Funded course', 'Short course', ...courses.map(c => c.offer_group).filter(Boolean)])].map(o => <option key={o} value={o} />)}
              </datalist>
              <p className="text-xs text-slate-400">Groups courses on the Pipeline so you can see which offers make money.</p>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-700">Accreditation (optional)</label>
              <Input
                value={courseForm.accreditation}
                onChange={(e) => setCourseForm(p => ({ ...p, accreditation: e.target.value }))}
                placeholder="e.g. VTCT Level 3"
                className="rounded-xl"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">Price (£)</label>
                <Input type="number" min="0" step="0.01" inputMode="decimal" value={courseForm.price} onChange={(e) => setCourseForm(p => ({ ...p, price: e.target.value }))} placeholder="e.g. 1850" className="rounded-xl" />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">Cost to deliver per student (£)</label>
                <Input type="number" min="0" step="0.01" inputMode="decimal" value={courseForm.cost_per_student} onChange={(e) => setCourseForm(p => ({ ...p, cost_per_student: e.target.value }))} placeholder="kit, tutor, exam fees" className="rounded-xl" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">Start Date</label>
                <Input type="date" value={courseForm.start_date} onChange={(e) => setCourseForm(p => ({ ...p, start_date: e.target.value }))} className="rounded-xl" />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">End Date</label>
                <Input type="date" value={courseForm.end_date} onChange={(e) => setCourseForm(p => ({ ...p, end_date: e.target.value }))} className="rounded-xl" />
              </div>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-700">Class Days</label>
              <div className="flex gap-2 flex-wrap">
                {DAYS_OF_WEEK.map((day) => (
                  <button
                    key={day.value}
                    className={`px-3 py-1.5 rounded-xl text-xs font-medium transition-all ${
                      courseForm.class_days.includes(day.value) ? 'bg-pink-500 text-white' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
                    }`}
                    onClick={() => toggleDay(day.value)}
                  >
                    {day.label}
                  </button>
                ))}
              </div>
            </div>
            {courseForm.class_days.length > 1 && (
              <label className="flex items-center justify-between gap-3 rounded-xl bg-slate-50 px-3 py-2.5 cursor-pointer">
                <span className="text-sm text-slate-700">Same time every day</span>
                <Switch checked={!courseForm.per_day_times} onCheckedChange={togglePerDayTimes} />
              </label>
            )}
            {courseForm.per_day_times && courseForm.class_days.length > 1 ? (
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">Times for each day</label>
                {DAY_ORDER.filter(d => courseForm.class_days.includes(d)).map(day => {
                  const t = courseForm.day_schedule[day] || { start: courseForm.class_time_start, end: courseForm.class_time_end };
                  return (
                    <div key={day} className="grid grid-cols-[3rem_1fr_1fr] items-center gap-2">
                      <span className="text-sm font-medium text-slate-600">{shortDay(day)}</span>
                      <Input type="time" aria-label={`${shortDay(day)} start time`} value={t.start} onChange={(e) => setDayTime(day, 'start', e.target.value)} className="rounded-xl" />
                      <Input type="time" aria-label={`${shortDay(day)} end time`} value={t.end} onChange={(e) => setDayTime(day, 'end', e.target.value)} className="rounded-xl" />
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-700">Start Time</label>
                  <Input type="time" value={courseForm.class_time_start} onChange={(e) => setCourseForm(p => ({ ...p, class_time_start: e.target.value }))} className="rounded-xl" />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-700">End Time</label>
                  <Input type="time" value={courseForm.class_time_end} onChange={(e) => setCourseForm(p => ({ ...p, class_time_end: e.target.value }))} className="rounded-xl" />
                </div>
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">Capacity</label>
                <Input type="number" value={courseForm.capacity} onChange={(e) => setCourseForm(p => ({ ...p, capacity: parseInt(e.target.value) || 20 }))} className="rounded-xl" />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">Location (optional)</label>
                <Input value={courseForm.location} onChange={(e) => setCourseForm(p => ({ ...p, location: e.target.value }))} placeholder="e.g. Room 3" className="rounded-xl" />
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" className="rounded-xl" onClick={() => setCreateDialog(false)}>Cancel</Button>
            <Button className="rounded-xl bg-pink-500 hover:bg-pink-600 text-white" onClick={handleSaveCourse} disabled={creating}>
              {creating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : editingCourse ? <CheckCircle className="w-4 h-4 mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
              {editingCourse ? 'Save Changes' : 'Create Course'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CoursesPage;
